import React, { useEffect, useState } from "react";

const backdropStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0, 0, 0, 0.75)",
  zIndex: 1050,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: "16px",
};

const modalStyle = {
  width: "100%",
  height: "100%",
  maxWidth: "1400px",
  background: "#fff",
  borderRadius: "10px",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
};

const headerStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "12px 16px",
  borderBottom: "1px solid #eee",
  gap: "12px",
};

const titleStyle = {
  margin: 0,
  fontSize: "16px",
  fontWeight: 600,
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
};

const closeButtonStyle = {
  border: "none",
  background: "#f3f4f6",
  borderRadius: "6px",
  width: "44px",
  height: "44px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  color: "#111827",
};

const closeIconStyle = {
  width: "22px",
  height: "22px",
  display: "block",
};

const actionsStyle = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
};

const openButtonStyle = {
  border: "none",
  background: "#2fac66",
  color: "#fff",
  borderRadius: "6px",
  padding: "8px 10px",
  cursor: "pointer",
  textDecoration: "none",
  fontSize: "13px",
};

const bodyStyle = {
  flex: 1,
  minHeight: 0,
  position: "relative",
};

const loadingOverlayStyle = {
  position: "absolute",
  inset: 0,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  gap: "10px",
  background: "rgba(255, 255, 255, 0.95)",
  zIndex: 1,
};

const spinnerStyle = {
  width: "36px",
  height: "36px",
  borderRadius: "50%",
  border: "3px solid #d1d5db",
  borderTopColor: "#2fac66",
  animation: "externalModalSpin 0.8s linear infinite",
};

const fallbackStyle = {
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  gap: "10px",
  padding: "24px",
};

const linkButtonStyle = {
  display: "inline-block",
  background: "#2fac66",
  color: "#fff",
  padding: "10px 14px",
  borderRadius: "6px",
  textDecoration: "none",
};

const stripControlChars = (value) =>
  Array.from(value).filter((char) => {
    const code = char.charCodeAt(0);
    return code > 31 && code !== 127 && (code < 128 || code > 159);
  }).join("");

const normalizeCp1252Punctuation = (value) =>
  value
    .replace(/\u0091|\u0092/g, "'")
    .replace(/\u0093|\u0094/g, '"')
    .replace(/\u0096|\u0097/g, "-");

const fullyDecodeUrl = (value, maxRounds = 5) => {
  let current = value;
  for (let round = 0; round < maxRounds; round += 1) {
    try {
      const decoded = decodeURIComponent(current);
      if (decoded === current) break;
      current = decoded;
    } catch {
      break;
    }
  }
  return current;
};

const sanitizeExternalUrl = (rawUrl) => {
  if (typeof rawUrl !== "string") return "";

  const cleaned = normalizeCp1252Punctuation(fullyDecodeUrl(stripControlChars(rawUrl)))
    .replace(/\s+/g, "")
    .trim();

  if (!cleaned) return "";

  const withProtocol = /^https?:\/\//i.test(cleaned)
    ? cleaned
    : `https://${cleaned.replace(/^\/+/, "")}`;

  try {
    const parsed = new URL(withProtocol);
    return parsed.toString();
  } catch {
    return "";
  }
};

const toYouTubeEmbedUrl = (url) => {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase().replace(/^www\./, "");
    let videoId = "";

    if (host === "youtu.be") {
      videoId = parsed.pathname.replace(/^\//, "");
    } else if (host.endsWith("youtube.com")) {
      if (parsed.pathname === "/watch") {
        videoId = parsed.searchParams.get("v") || "";
      } else if (parsed.pathname.startsWith("/shorts/")) {
        videoId = parsed.pathname.split("/")[2] || "";
      } else if (parsed.pathname.startsWith("/embed/")) {
        return parsed.toString();
      }
    }

    if (!videoId) return url;

    const embedUrl = new URL(`https://www.youtube.com/embed/${videoId}`);
    embedUrl.searchParams.set("rel", "0");
    embedUrl.searchParams.set("modestbranding", "1");
    return embedUrl.toString();
  } catch {
    return url;
  }
};

const getPreviewUrl = (safeUrl, mediaType) => {
  if (!safeUrl) return "";
  if (mediaType !== "video") return safeUrl;
  return toYouTubeEmbedUrl(safeUrl);
};

const ExternalArticleModal = ({ isOpen, url, title, mediaType = "article", onClose }) => {
  const safeUrl = sanitizeExternalUrl(url);
  const previewUrl = getPreviewUrl(safeUrl, mediaType);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onEsc = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", onEsc);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener("keydown", onEsc);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    setIsLoading(Boolean(isOpen && previewUrl));
  }, [isOpen, previewUrl]);

  if (!isOpen || !url) return null;

  return (
    <div style={backdropStyle} onClick={onClose} role="presentation">
      <div style={modalStyle} onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true" aria-label={title || "Article externe"}>
        <div style={headerStyle}>
          <h4 style={titleStyle}>{title || "Article externe"}</h4>
          <div style={actionsStyle}>
            <a href={safeUrl || "#"} target="_blank" rel="noopener noreferrer" style={openButtonStyle}>
              Ouvrir
            </a>
            <button type="button" onClick={onClose} style={closeButtonStyle} aria-label="Fermer">
              <svg viewBox="0 0 24 24" aria-hidden="true" style={closeIconStyle}>
                <path
                  d="M6 6L18 18M18 6L6 18"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        </div>
        <div style={bodyStyle}>
          <style>
            {"@keyframes externalModalSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }"}
          </style>
          {isLoading && previewUrl && (
            <div style={loadingOverlayStyle}>
              <div style={spinnerStyle} />
              <p style={{ margin: 0 }}>Chargement de la page...</p>
            </div>
          )}
          {previewUrl ? (
            <iframe
              title={title || "Article externe"}
              src={previewUrl}
              onLoad={() => setIsLoading(false)}
              style={{ width: "100%", height: "100%", border: "none" }}
            />
          ) : (
            <div style={fallbackStyle}>
              <p>L&apos;URL de cet article est invalide.</p>
            </div>
          )}
          <noscript>
            <div style={fallbackStyle}>
              <p>L&apos;aperçu intégré n&apos;est pas disponible.</p>
              <a href={safeUrl || "#"} target="_blank" rel="noopener noreferrer" style={linkButtonStyle}>
                Ouvrir l&apos;article
              </a>
            </div>
          </noscript>
        </div>
      </div>
    </div>
  );
};

export default ExternalArticleModal;
